import {
  RevisionConflictError,
  type TransactionResult,
  type JsonValue,
  type WorldDocument,
  type WorldEvent,
  type WorldEventDiff,
  type WorldValueDiff,
  type WorldTransaction,
} from "./types";

const clone = <T>(value: T): T => structuredClone(value);
const isObject = (value: JsonValue | undefined): value is { [key: string]: JsonValue } => Boolean(value && typeof value === "object" && !Array.isArray(value));
const legacyRuntimeFields: Record<string, string[]> = {
  character: ["role", "isPlayer", "currentLocationId", "coordinates"],
  location: ["status", "discovered"],
  scene: ["status", "startedAt", "committedAt", "consequences"],
  world_clock: ["currentTime", "elapsedMinutes", "timezone", "time", "period", "calendar", "day", "year", "weather"],
};

/** Moves legacy engine-owned fields out of creator schema data on read. */
export function normalizeWorldRuntimeState(document: WorldDocument): WorldDocument {
  if (document.fileTypeId && !document.fileTypeId.startsWith("core.")) return clone(document);
  const keys = legacyRuntimeFields[document.kind] ?? [];
  if (!keys.length) return clone(document);
  const fields = clone(document.fields);
  const runtimeState = clone(document.runtimeState ?? {});
  let changed = false;
  for (const key of keys) {
    if (!(key in fields)) continue;
    if (!(key in runtimeState)) runtimeState[key] = fields[key]!;
    delete fields[key];
    changed = true;
  }
  return changed || Object.keys(runtimeState).length ? { ...clone(document), fields, runtimeState } : clone(document);
}

function collectValueDiff(before: JsonValue | undefined, after: JsonValue | undefined, path: string[], output: WorldValueDiff[], limit: number): void {
  if (output.length >= limit || JSON.stringify(before) === JSON.stringify(after)) return;
  if (isObject(before) && isObject(after)) {
    for (const key of [...new Set([...Object.keys(before), ...Object.keys(after)])].sort()) {
      collectValueDiff(before[key], after[key], [...path, key], output, limit);
      if (output.length >= limit) break;
    }
    return;
  }
  output.push({ path, kind: before === undefined ? "added" : after === undefined ? "removed" : "changed", ...(before === undefined ? {} : { before: clone(before) }), ...(after === undefined ? {} : { after: clone(after) }) });
}

/** Produces a bounded, presentation-neutral receipt from canonical snapshots. */
export function diffWorldEvent(event: WorldEvent, limit = 128): WorldEventDiff {
  const changes: WorldValueDiff[] = [];
  const before = event.before;
  const after = event.after;
  if (!before || !after) {
    changes.push({ path: [], kind: before ? "removed" : "added", ...(before ? { before: before as unknown as JsonValue } : {}), ...(after ? { after: after as unknown as JsonValue } : {}) });
  } else {
    const beforeProjection: JsonValue = { title: before.title, summary: before.summary, fields: before.fields, runtimeState: before.runtimeState ?? {}, references: before.references as unknown as JsonValue, visibility: before.visibility, ...(before.deletedAt ? { deletedAt: before.deletedAt } : {}) };
    const afterProjection: JsonValue = { title: after.title, summary: after.summary, fields: after.fields, runtimeState: after.runtimeState ?? {}, references: after.references as unknown as JsonValue, visibility: after.visibility, ...(after.deletedAt ? { deletedAt: after.deletedAt } : {}) };
    collectValueDiff(beforeProjection, afterProjection, [], changes, Math.max(1, Math.min(512, Math.trunc(limit))));
  }
  return { transactionId: event.transactionId, eventIndex: event.eventIndex, documentId: event.documentId, operation: event.operation, actor: event.actor, ...(event.reason ? { reason: event.reason } : {}), sourceMessageIds: [...event.sourceMessageIds], changes, createdAt: event.createdAt };
}

export function validateWorldDocument(document: WorldDocument): string[] {
  const errors: string[] = [];
  if (!document.id.trim()) errors.push("id is required");
  if (!document.storyId.trim()) errors.push("storyId is required");
  if (!document.kind.trim() || document.kind.length > 96 || !/^[\p{L}\p{N}][\p{L}\p{N}_.:-]*$/u.test(document.kind)) errors.push(`invalid kind: ${document.kind}`);
  if (!Number.isInteger(document.schemaVersion) || document.schemaVersion < 1) errors.push("schemaVersion must be positive");
  if (!Number.isInteger(document.revision) || document.revision < 1) errors.push("revision must be positive");
  if (!document.title.trim()) errors.push("title is required");
  const duplicateReferences = new Set<string>();
  for (const reference of document.references) {
    const key = `${reference.relation}:${reference.targetId}`;
    if (!reference.relation.trim() || !reference.targetId.trim()) errors.push("references need relation and targetId");
    if (duplicateReferences.has(key)) errors.push(`duplicate reference: ${key}`);
    duplicateReferences.add(key);
  }
  return errors;
}

export function applyWorldTransaction(
  currentDocuments: readonly WorldDocument[],
  transaction: WorldTransaction,
): TransactionResult {
  const next = new Map(currentDocuments.map((document) => [document.id, clone(document)]));
  const events: WorldEvent[] = [];
  const seen = new Set<string>();

  transaction.mutations.forEach((mutation, eventIndex) => {
    if (seen.has(mutation.documentId)) throw new Error(`Transaction mutates ${mutation.documentId} more than once`);
    seen.add(mutation.documentId);
    const before = next.get(mutation.documentId);
    const actualRevision = before?.revision ?? 0;
    if (mutation.expectedRevision !== actualRevision) {
      throw new RevisionConflictError(mutation.documentId, mutation.expectedRevision, actualRevision);
    }

    let after: WorldDocument | undefined;
    if (mutation.operation === "delete") {
      if (!before) throw new Error(`Cannot delete missing document: ${mutation.documentId}`);
      after = { ...clone(before), revision: before.revision + 1, updatedAt: transaction.createdAt, deletedAt: transaction.createdAt };
      next.set(after.id, after);
    } else {
      if (!mutation.document) throw new Error(`${mutation.operation} requires document payload`);
      if (mutation.document.id !== mutation.documentId) throw new Error("Mutation documentId does not match payload id");
      if (mutation.document.storyId !== transaction.storyId) throw new Error("Document belongs to another story");
      if (mutation.operation === "create" && before) throw new Error(`Document already exists: ${mutation.documentId}`);
      if (mutation.operation === "replace" && !before) throw new Error(`Cannot replace missing document: ${mutation.documentId}`);
      after = {
        ...clone(mutation.document),
        revision: actualRevision + 1,
        createdAt: before?.createdAt ?? mutation.document.createdAt,
        updatedAt: transaction.createdAt,
      };
      const errors = validateWorldDocument(after);
      if (errors.length) throw new Error(`Invalid world document: ${errors.join(", ")}`);
      next.set(after.id, after);
    }

    events.push({
      transactionId: transaction.id,
      eventIndex,
      storyId: transaction.storyId,
      actor: transaction.actor,
      operation: mutation.operation,
      documentId: mutation.documentId,
      beforeRevision: actualRevision,
      afterRevision: after.revision,
      ...(before ? { before: clone(before) } : {}),
      after: clone(after),
      ...(mutation.reason ? { reason: mutation.reason } : {}),
      sourceMessageIds: [...(mutation.sourceMessageIds ?? [])],
      createdAt: transaction.createdAt,
    });
  });

  return { documents: [...next.values()], events };
}

export function inverseTransaction(events: readonly WorldEvent[], transactionId: string, createdAt: string): WorldTransaction {
  if (!events.length) throw new Error("Cannot invert an empty event list");
  const ordered = [...events].sort((a, b) => b.eventIndex - a.eventIndex);
  return {
    id: transactionId,
    storyId: ordered[0]!.storyId,
    actor: "system",
    createdAt,
    mutations: ordered.map((event) => {
      if (!event.before) {
        return { operation: "delete", documentId: event.documentId, expectedRevision: event.afterRevision, reason: `Undo ${event.transactionId}` };
      }
      return {
        operation: "replace",
        documentId: event.documentId,
        expectedRevision: event.afterRevision,
        document: { ...clone(event.before), revision: event.afterRevision },
        reason: `Undo ${event.transactionId}`,
      };
    }),
  };
}

export interface WorldBacklink {
  sourceDocumentId: string;
  sourceKind: string;
  sourceTitle: string;
  relation: string;
  metadata?: JsonValue;
}

/** Computes bidirectional backlinks pointing to a given target document id. */
export function computeBacklinks(documents: readonly WorldDocument[], targetDocumentId: string): WorldBacklink[] {
  const backlinks: WorldBacklink[] = [];
  for (const document of documents) {
    if (document.deletedAt || document.id === targetDocumentId) continue;
    for (const reference of document.references) {
      if (reference.targetId === targetDocumentId) {
        backlinks.push({
          sourceDocumentId: document.id,
          sourceKind: document.kind,
          sourceTitle: document.title,
          relation: reference.relation,
          ...(reference.metadata ? { metadata: reference.metadata } : {}),
        });
      }
    }
  }
  return backlinks;
}
